import React, { useEffect, useState, ChangeEvent, useRef, useCallback, useMemo } from 'react';
import { AnyIfEmpty, useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import SearchIcon from '../../images/icon-search.svg';
import {
    clearPreviousLocationData,
    FileState,
    getFilteredLocation,
    getLocation,
    getSearchLocation,
    importProperties,
} from '../../redux/location/actions/location.action';
import { AppDispatch, IRootState } from '../../redux/rootReducer';
import Loader from '../common/loader/Loader';
import Toast from '../common/toast/Toast';
import UserName from '../common/user/UserName';
import LocationCard from './locationCard/LocationCard';
import Select from 'react-select';
import styles from './Locations.module.css';
import TinyLoader from '../common/tiny-loader/TinyLoader';
import iconClose from '../../images/icon-close.svg';
import CustModal from '../common/custmodal/CustModal';
import xlsIcon from '../../images/xls-icon.svg';
import ExcelIcon from '../svgIcons/ExcelIcon';
import { useDebounce } from '../../utils/useDebounce';
import ReactDOM from 'react-dom/client';
import Visitor from '../../pages/Visitor';
import { hasPermission } from '../auth/login/Login';
import {
    getLocationsComprehensive,
    resetLocationsComprehensiveMessage,
} from '../../redux/locationsComprehensive/actions/locationsComprehensive.actions';

export const bytesToMB = (bytes: number): number => {
    return bytes / (1024 * 1024);
};

export interface UserProps {
    email: string;
    role: 'admin' | 'super_admin';
    fullName: string;
}

export interface locationsData {
    id?: string;
    created_at: Date;
    updated_at: Date;
    name: string;
    street1: string;
    street2: string;
    city: string;
    state: string;
    country?: string;
    latitude?: string;
    longitude?: string;
    adminId: string;
    images: string[];
    timeZone: string;
    description: string;
    confirmText: string;
    provisioningId: number;
    masterCode: string;
    mapUrl: string;
    pmsPropertyId?: string;
    yardiId: string;
    listOfPOI: string;
}

const locationFilter = [{ title: 'Show All' }, { title: 'No Availability' }, { title: 'Property With Availability' }];

export const colourStyles = {
    control: (base: AnyIfEmpty<object>) => ({
        ...base,
        height: 48,
        minHeight: 48,
        maxHeight: 48,
        outline: 'none',
    }),
    menu: (base: AnyIfEmpty<object>) => ({
        ...base,
        zIndex: 9,
    }),
    option: (styles: AnyIfEmpty<object>, { isDisabled, isFocused, isSelected }: AnyIfEmpty<object>) => {
        return {
            ...styles,
            backgroundColor: isSelected ? '#51ab86 !important' : isFocused ? '#DEEBFF' : null,
            color: isSelected ? '#fff !important' : '#000',
            cursor: isDisabled ? 'not-allowed' : 'default',
        };
    },
};

const Location = () => {
    const { search } = useLocation();
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const canCreateProperty = hasPermission('Properties & Units', 'Property', 'create');
    const canDeleteProperty = hasPermission('Properties & Units', 'Property', 'delete');
    const canReadProperty = hasPermission('Properties & Units', 'Property', 'read');
    const canUpdateProperty = hasPermission('Properties & Units', 'Property', 'update');
    const allLocationsList = useSelector((state: IRootState) => state.locationReducer.data.locations);
    const invitedLocations = useSelector((state: IRootState) => state.locationReducer.data?.locationinvitedlist);
    const locationLoading = useSelector((state: IRootState) => state.locationReducer.loading);
    const error = useSelector((state: IRootState) => state.locationReducer.errorMsg);
    const {
        loading: locationsComprehensiveLoading,
        error: locationsComprehensiveError,
        data: locationsComprehensiveMessage,
    } = useSelector((state: IRootState) => state.locationsComprehensiveReducer);
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    const [showToast, setShowToast] = useState({ show: false, type: '' });
    const [toastMessage, setToastMessage] = useState('');
    const [locations, setLocations] = useState<locationsData[]>([]);
    const [searchUnits, setSearchUnits] = useState('');
    const [selectedFilter, setSelectedFilter] = useState({ value: '', label: '' });
    const [bulkImportModal, setBulkImportModal] = useState(false);
    const [excelFile, setExcelFile] = useState<FileState>({ file: null });
    const [isLoading, setIsLoading] = useState(false);
    const [modalStep, setModalStep] = useState(1);
    const [searchText, setSearchText] = useState(search ? decodeURIComponent(search.replace('?search=', '').replace(/\+/g, ' ')) : '');
    const searchWithDebounce = useDebounce(searchText.trim(), 1000);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [addChildren, setAddChildren] = useState(true);
    const hasRestoredScroll = useRef(false); // Track if scroll has been restored
    const previousSearchRef = useRef<string>('');
    const previousFilterRef = useRef<string>('');
    const fetchedOnceRef = useRef(false);

    // Memoize merged locations to prevent unnecessary re-renders
    const mergedLocations = useMemo(() => {
        setIsLoading(true);
        if (!invitedLocations?.length && !allLocationsList?.length) {

            return [];
        }
        if (invitedLocations?.length || allLocationsList?.length) {
            const data = invitedLocations?.map((list: locationsData) => {
                return {
                    ...list,
                    isRestricted: true,
                };
            });
            return [...data, ...allLocationsList];
        }
        return [];
    }, [invitedLocations, allLocationsList]);

    const setMergedLocations = async () => {
        await setLocations(mergedLocations);
        setIsLoading(false);
    }

    // Update locations state only when merged data changes
    useEffect(() => {
        setMergedLocations();
    }, [mergedLocations]);

    // Restore scroll position when coming back from detail page
    useEffect(() => {
        const savedScrollPosition = sessionStorage.getItem('locationsScrollPosition');

        // Restore scroll only once and only if we have locations loaded
        if (savedScrollPosition && !hasRestoredScroll.current && !locationLoading && mergedLocations.length > 0) {
            const scrollPosition = parseInt(savedScrollPosition, 10);

            // Use setTimeout to ensure DOM is fully rendered
            setTimeout(() => {
                window.scrollTo({
                    top: scrollPosition,
                    behavior: 'smooth',
                });
                hasRestoredScroll.current = true;
                // Clear the saved position after restoring
                sessionStorage.removeItem('locationsScrollPosition');
            }, 100);
        }
    }, [mergedLocations, locationLoading]);

    // Save scroll position periodically while on the page
    useEffect(() => {
        const handleScroll = () => {
            sessionStorage.setItem('locationsScrollPosition', window.scrollY.toString());
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        if (showToast?.show) {
            setTimeout(() => {
                setShowToast({ show: false, type: '' }), setToastMessage('');
            }, 4000);
        }
    }, [showToast]);

    useEffect(() => {
        if (locationsComprehensiveError) {
            setToast(locationsComprehensiveError, false, false);
            dispatch(resetLocationsComprehensiveMessage());
        }

        if (locationsComprehensiveMessage) {
            const message = typeof locationsComprehensiveMessage === 'string' && locationsComprehensiveMessage;

            if (message) {
                setToast('File downloaded successfully', true, false);
                dispatch(resetLocationsComprehensiveMessage());
            }
        }
    }, [locationsComprehensiveError, locationsComprehensiveMessage]);

    useEffect(() => {
        if (error) {
            setToast(error, false, false);
        }
    }, [error]);

    // const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    //     setSearchText(e.target.value);
    // };

    // useEffect(() => {
    //     if (canReadProperty) {
    //         dispatch(
    //             getSearchLocation(
    //                 searchWithDebounce,
    //                 selectedFilter.label === 'No Availability'
    //                     ? 'false'
    //                     : selectedFilter.label === 'Property With Availability'
    //                         ? 'true'
    //                         : '',
    //             ),
    //         );
    //     }

    //     const params = new URLSearchParams();
    //     if (searchWithDebounce) {
    //         params.set('search', searchWithDebounce);
    //     } else {
    //         params.delete('search');
    //     }
    //     navigate(`?${params.toString()}`);
    // }, [searchWithDebounce, canReadProperty, selectedFilter.label]);

    useEffect(() => {
        if (iframeRef.current) {
            const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;

            if (iframeDoc) {
                // Clear existing content
                iframeDoc.body.innerHTML = '';

                // Create a root and render the React component inside the iframe
                const root = ReactDOM.createRoot(iframeDoc.body); // Use ReactDOM.createRoot
                root.render(<Visitor />);
            }
        }
    }, [addChildren]);

    const setToast = (message: string, success: boolean, closeModal = true) => {
        setToastMessage(message);
        closeModal && success && setBulkImportModal(false);
        setShowToast({ show: true, type: success ? 'success' : 'delete' });
        closeModal && dispatch(getLocation());
    };

    const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            if (selectedFile?.size > 5 * 1024 * 1024) {
                setToastMessage('File size exceeds 5 MB limit');
                setShowToast({ show: true, type: 'delete' });
                setExcelFile({ file: null });
            } else if (
                !(
                    selectedFile?.type.toString() === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                    selectedFile?.type?.toString() === 'application/vnd.ms-excel'
                )
            ) {
                setToastMessage('Please select .xls Or .xlsx file');
                setShowToast({ show: true, type: 'delete' });
                setExcelFile({ file: null });
            } else {
                setExcelFile({ file: selectedFile });
            }
        }
    };

    const onCloseModal = () => {
        setToastMessage('');
        setExcelFile({ file: null });
        setShowToast({ show: false, type: '' });
        setBulkImportModal(false);
        setModalStep(1);
    };

    const handleImport = () => {
        try {
            if (excelFile?.file) {
                setIsLoading(true);
                dispatch(importProperties(excelFile?.file, setToast, setExcelFile, setModalStep, setIsLoading));
            }
        } catch (err) {
            setIsLoading(false);
        }
    };

    const ModalContent = () => {
        return (
            <>
                <div className={styles.modalHeaderContainer}>
                    <h2 className={styles.modalHeader}>Bulk Import</h2>
                    <div className={`${styles.close} ${isLoading ? styles.disabled : ''}`}>
                        <img src={iconClose} onClick={() => (!isLoading ? onCloseModal() : null)} alt="Close" />
                    </div>
                </div>

                <div className={styles.modalBodyContainer}>
                    {!excelFile?.file && modalStep === 1 && (
                        <div className={styles.fileUploadContainer}>
                            <input
                                disabled={locationLoading}
                                id="fileInput"
                                name="fileInput"
                                className={styles.fileInput}
                                type="file"
                                accept=".xls, .xlsx"
                                onChange={handleFileUpload}
                            />
                            {/* <img src={cloudUploadIcon} alt='cloud-upload-img' width={34} height={34} /> */}
                            <ExcelIcon fill="#51ab86" />
                            <p className={styles.primaryText}>
                                Drag and Drop file or{' '}
                                <span>
                                    <u>Click to Upload</u>
                                </span>
                            </p>
                            <p className={styles.primaryText}>Max size: 5 MB | .xls, .xlsx formats allowed</p>
                        </div>
                    )}

                    {modalStep === 1 && excelFile?.file && (
                        <div className={styles.fileContainer}>
                            <div className={styles.iconContainer}>
                                <img src={xlsIcon} alt="excel icon" width={37.08} height={37.08} />
                            </div>
                            <div className={styles.fileDetailContainer}>
                                <p className={styles.primaryText}>{excelFile?.file?.name}</p>
                                <p className={styles.secondaryText}>
                                    {excelFile?.file ? bytesToMB(excelFile.file.size).toFixed(3) + ' MB' : null}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles.buttonsContainer}>
                    <button className={styles.cancelBtn} onClick={onCloseModal} disabled={isLoading}>
                        Cancel
                    </button>

                    {modalStep === 1 && excelFile?.file && (
                        <button className={styles.importBtn} onClick={handleImport} disabled={isLoading}>
                            {isLoading ? (
                                <div className={styles.loaderContainer}>
                                    <TinyLoader />
                                </div>
                            ) : (
                                <span>Import file</span>
                            )}
                        </button>
                    )}
                </div>
            </>
        );
    };

    // Prevent duplicate API calls - only fetch when search or filter actually changes
    useEffect(() => {
        const currentSearch = searchWithDebounce || '';
        const currentFilter = selectedFilter.label || '';
        const searchChanged = previousSearchRef.current !== currentSearch;
        const filterChanged = previousFilterRef.current !== currentFilter;

        // Skip if neither search nor filter changed (initial render or other updates)
        if (!searchChanged && !filterChanged && fetchedOnceRef.current) {
            return;
        }

        if (canReadProperty) {
            dispatch(
                getSearchLocation(
                    currentSearch,
                    currentFilter === 'No Availability'
                        ? 'false'
                        : currentFilter === 'Property With Availability'
                            ? 'true'
                            : '',
                ),
            );
            previousSearchRef.current = currentSearch;
            previousFilterRef.current = currentFilter;
            fetchedOnceRef.current = true;
        }

        // Update URL params
        const params = new URLSearchParams();
        if (currentSearch) {
            params.set('search', currentSearch);
        } else {
            params.delete('search');
        }
        navigate(`?${params.toString()}`, { replace: true });
    }, [searchWithDebounce, canReadProperty, selectedFilter.label, dispatch, navigate]);

    const onLocationClick = useCallback((id: string) => {
        // Save current scroll position before navigating
        sessionStorage.setItem('locationsScrollPosition', window.scrollY.toString());

        dispatch(clearPreviousLocationData());
        navigate(`/location/${id}`, { state: { search: searchWithDebounce } });
    }, [dispatch, navigate, searchWithDebounce]);

    const exportProperty = useCallback(() => {
        dispatch(getLocationsComprehensive());
    }, [dispatch]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trimStart(); // removes starting spaces only
        setSearchText(value);
    };

    return (
        <>
            {showToast?.show && toastMessage?.length && <Toast type={showToast?.type} message={toastMessage} />}
            {(locationLoading || locationsComprehensiveLoading) && <Loader />}
            {!locationLoading && (
                <div className={`outletConainer ${styles.locationsContainer}`}>
                    <div className={styles.unitsContainer}>
                        <div className={styles.unitsHead}>
                            <div className={styles.userGreet}>
                                <p>
                                    Welcome <UserName />
                                </p>
                                <h2>Properties</h2>
                            </div>
                            <div className={styles.SearchWrapper}>
                                {canCreateProperty && (
                                    <button
                                        className={styles.btnGreen}
                                        onClick={() => {
                                            navigate(`/add-location`, {
                                                state: { type: 'add' },
                                            });
                                        }}>
                                        + Add Property
                                    </button>
                                )}

                                {userInfo && userInfo.role === 'super_admin' && (
                                    <button
                                        className={styles.btnGreen}
                                        onClick={() => {
                                            exportProperty();
                                        }}>
                                        Export Property List
                                    </button>
                                )}

                                {/* {canCreateProperty && (<button
                                    className={styles.btnGreen}
                                    onClick={() => {
                                        setBulkImportModal(true);
                                    }}>
                                    Bulk Import
                                </button>)} */}
                                {/* </div> */}

                                <div className={styles.filterDropDown}>
                                    <Select
                                        onChange={(selectedOption) => {
                                            if (selectedOption?.value) {
                                                setSelectedFilter(selectedOption);
                                            }
                                        }}
                                        isSearchable={false}
                                        classNamePrefix="custom-react-select"
                                        value={
                                            selectedFilter.value !== ''
                                                ? { value: selectedFilter.value, label: selectedFilter.label }
                                                : undefined
                                        }
                                        options={locationFilter.map((location: { title: string }) => {
                                            return {
                                                label: location.title,
                                                value: location.title,
                                            };
                                        })}
                                        placeholder="Select Option"
                                        styles={colourStyles}
                                    />
                                </div>

                                <div className={styles.search}>
                                    <input
                                        onChange={handleChange}
                                        type="text"
                                        placeholder="Search for Property"
                                        maxLength={50}
                                        value={searchText}
                                    />
                                    <img className={styles.searchIconImage} src={SearchIcon} alt="search-icon" />
                                </div>
                            </div>
                        </div>

                        {(locationLoading || isLoading) ? (
                            <div className={styles.noRecordsWrapper}>
                                <div className={styles.noRecordsContent}>
                                    <p>Loading ...</p>
                                </div>
                            </div>
                        ) : canReadProperty && locations?.length > 0 ? (
                            <div className={styles.locationWrapper}>
                                {locations.map((location: AnyIfEmpty<object>, index: number) => (
                                    <LocationCard
                                        canEditProperty={canUpdateProperty || false}
                                        canDeleteProperty={canDeleteProperty || false}
                                        key={index}
                                        location={location}
                                        onClick={() => onLocationClick(location?.id)}
                                    />
                                ))}
                            </div>
                        ) : (
                            canReadProperty && !isLoading && locations?.length === 0 && (
                                <div className={styles.noRecordsWrapper}>
                                    <div className={styles.noRecordsContent}>
                                        <p>No Records Found</p>
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                </div>
            )}

            <CustModal
                className="custom-import-modal"
                open={bulkImportModal}
                setOpen={setBulkImportModal}
                bodyData={<ModalContent />}
                onCloseModal={onCloseModal}
            />
        </>
    );
};

export default Location;
